/**
 * Duplicate phone numbers on the Employees page.
 *
 * A phone number on more than one employee record (active or inactive) causes mobile sign-in
 * failures that surface as a generic "invalid" message, so every shared number is listed for
 * cleanup — each number should belong to exactly one employee record.
 */

export interface DuplicatePhoneGroup {
    key: string;
    activeCount: number;
    count: number;
}

/** normalized phone → its group; only phones on 2+ employee records. */
export type DuplicatePhoneGroups = Map<string, DuplicatePhoneGroup>;

/** Digits only, US country code dropped. Blank, too-short, and one-repeated-digit values are null. */
export function normalizePhone(phone: string | null | undefined): string | null {
    if (!phone) return null;
    let d = phone.replace(/\D/g, "");
    if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
    if (d.length < 7 || /^(\d)\1+$/.test(d)) return null;
    return d;
}

export function findDuplicatePhones(
    employees: readonly { phoneNumber?: string | null; active: boolean }[]
): DuplicatePhoneGroups {
    const tally = new Map<string, DuplicatePhoneGroup>();
    for (const e of employees) {
        const key = normalizePhone(e.phoneNumber);
        if (!key) continue;
        const g = tally.get(key) ?? { key, count: 0, activeCount: 0 };
        g.count++;
        if (e.active) g.activeCount++;
        tally.set(key, g);
    }
    const groups: DuplicatePhoneGroups = new Map();
    tally.forEach((g, key) => {
        if (g.count >= 2) groups.set(key, g);
    });
    return groups;
}

/** Sort key that keeps each phone's records together, active before inactive within a number. */
export function duplicateSortKey(group: DuplicatePhoneGroup, active: boolean): string {
    return `${group.key}|${active ? 0 : 1}`;
}
